import { useEffect, useState } from "react";
import { useParticipantInfo } from "@livekit/components-react";
import type { Participant } from "livekit-client";
import { Check, Link2 } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * "Only you here" empty state — shown by SpeakerLayout/SidebarLayout instead
 * of a bare avatar tile when the local user is solo with the camera off and
 * no screen-share active. Offers a one-tap "copy meeting link" so there's
 * something useful to do while waiting for others to join.
 */
export function SoloEmptyState({ participant }: { participant: Participant }) {
  const { t } = useTranslation();
  const { name, identity } = useParticipantInfo({ participant });
  const displayName = name?.trim() || prettyIdentity(identity ?? "");
  const initial = (displayName.trim()[0] ?? "?").toUpperCase();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const id = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(id);
  }, [copied]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
    } catch {
      // clipboard API can reject under insecure contexts; no-op
    }
  };

  return (
    <div className="h-full w-full flex flex-col items-center justify-center gap-5 px-6 text-center rounded-2xl overflow-hidden bg-muted">
      <div className="size-24 rounded-full flex items-center justify-center text-4xl font-bold bg-neutral-600 text-white">
        {initial}
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-base font-semibold text-foreground">{displayName}</span>
        <span className="text-sm text-muted-foreground">{t("call.speaker.onlyYouHere")}</span>
      </div>
      <button
        type="button"
        onClick={handleCopy}
        className={`mt-1 inline-flex items-center gap-2 px-5 h-11 rounded-full font-semibold transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
          copied ? "bg-primary/15 text-primary" : "bg-primary text-primary-foreground shadow-sm"
        }`}
      >
        {copied ? <Check className="size-4" /> : <Link2 className="size-4" />}
        <span>{copied ? t("call.speaker.copied") : t("call.speaker.copyMeetingLink")}</span>
      </button>
    </div>
  );
}

function prettyIdentity(identity: string): string {
  return identity.split("@")[0];
}
